(function () {
  'use strict';

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  class EffiloHeroScroll extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.surface = this.querySelector('[data-effilo-hero-surface]');
      this.track = this.querySelector('[data-effilo-hero-track]');
      this.indicators = this.querySelectorAll('[data-effilo-hero-indicator]');
      if (!this.surface || !this.track) return;

      this.pauseMs = Math.max(500, parseInt(this.dataset.pause, 10) || 3000);
      this.transitionMs = Math.max(200, parseInt(this.dataset.transition, 10) || 800);
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.mobileQuery = window.matchMedia('(max-width: 989px)');

      this.originalSlides = Array.prototype.slice.call(
        this.track.querySelectorAll('[data-effilo-hero-slide]')
      );
      if (this.originalSlides.length < 2 || this.reduceMotion) return;

      // Reverse visual direction by reversing slide order; transform always moves left.
      if (this.dataset.direction === 'reverse') {
        this.originalSlides
          .slice()
          .reverse()
          .forEach(
            function (slide) {
              this.track.appendChild(slide);
            }.bind(this)
          );
        this.originalSlides = Array.prototype.slice.call(
          this.track.querySelectorAll('[data-effilo-hero-slide]')
        );
      }

      this.originalSlides.forEach(
        function (slide) {
          var clone = slide.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('data-effilo-hero-slide');
          this.track.appendChild(clone);
        }.bind(this)
      );

      this.index = 0;
      this.running = false;
      this.hovered = false;
      this.visible = true;
      this.pageVisible = !document.hidden;

      this._onTransitionEnd = this._onTransitionEnd.bind(this);
      this._onMouseEnter = this._onMouseEnter.bind(this);
      this._onMouseLeave = this._onMouseLeave.bind(this);
      this._onVisibility = this._onVisibility.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onIndicatorClick = this._onIndicatorClick.bind(this);

      this.surface.addEventListener('transitionend', this._onTransitionEnd);
      this.addEventListener('mouseenter', this._onMouseEnter);
      this.addEventListener('mouseleave', this._onMouseLeave);
      document.addEventListener('visibilitychange', this._onVisibility);

      Array.prototype.forEach.call(this.indicators, function (button) {
        button.addEventListener('click', this._onIndicatorClick);
      }.bind(this));

      this.resizeObserver = new ResizeObserver(this._onResize);
      this.resizeObserver.observe(this);

      this.intersectionObserver = new IntersectionObserver(
        function (entries) {
          this.visible = entries[0] && entries[0].isIntersecting;
        }.bind(this),
        { threshold: 0.2 }
      );
      this.intersectionObserver.observe(this);

      this._syncSlideWidths();
      this._applyTransform(false);
      this._updateIndicators();
      this._startLoop();
    }

    disconnectedCallback() {
      this.running = false;
      if (this._loopToken) this._loopToken.cancelled = true;
      if (this.surface) {
        this.surface.removeEventListener('transitionend', this._onTransitionEnd);
      }
      this.removeEventListener('mouseenter', this._onMouseEnter);
      this.removeEventListener('mouseleave', this._onMouseLeave);
      document.removeEventListener('visibilitychange', this._onVisibility);
      if (this.indicators) {
        Array.prototype.forEach.call(
          this.indicators,
          function (button) {
            button.removeEventListener('click', this._onIndicatorClick);
          }.bind(this)
        );
      }
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
    }

    _isMobile() {
      return this.mobileQuery && this.mobileQuery.matches;
    }

    _displayIndex() {
      if (!this.originalSlides.length) return 0;
      return this.index % this.originalSlides.length;
    }

    _updateIndicators() {
      if (!this.indicators || !this.indicators.length) return;
      var active = this._displayIndex();
      Array.prototype.forEach.call(this.indicators, function (button, i) {
        var isActive = i === active;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        if (isActive) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });
    }

    _syncSlideWidths() {
      var slides = this.track.querySelectorAll('.effilo-hero__slide');
      if (!slides.length) return;

      if (this._isMobile()) {
        var width = Math.round(this.getBoundingClientRect().width);
        if (width <= 0) return;
        Array.prototype.forEach.call(slides, function (slide) {
          slide.style.flex = '0 0 ' + width + 'px';
          slide.style.width = width + 'px';
          slide.style.maxWidth = width + 'px';
        });
        this.track.style.gap = '0px';
      } else {
        Array.prototype.forEach.call(slides, function (slide) {
          slide.style.flex = '';
          slide.style.width = '';
          slide.style.maxWidth = '';
        });
        this.track.style.gap = '';
      }
    }

    _stepSize() {
      if (this._isMobile()) {
        return Math.round(this.getBoundingClientRect().width);
      }
      var slide = this.track.querySelector('[data-effilo-hero-slide]');
      if (!slide) return 0;
      var styles = window.getComputedStyle(this.track);
      var gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return slide.getBoundingClientRect().width + gap;
    }

    _applyTransform(animate) {
      var step = this._stepSize();
      var offset = -this.index * step;
      this.surface.style.transition = animate
        ? 'transform ' + this.transitionMs + 'ms ease-in-out'
        : 'none';
      this.surface.style.transform = 'translate3d(' + offset + 'px, 0, 0)';
      this._updateIndicators();
    }

    _goTo(index, animate) {
      var total = this.originalSlides.length;
      if (total < 1) return;
      this.index = ((index % total) + total) % total;
      if (animate) {
        this._advancing = true;
        if (this._loopToken) this._loopToken.cancelled = true;
        this._applyTransform(true);
      } else {
        this._advancing = false;
        this._applyTransform(false);
        this._startLoop();
      }
    }

    _canAdvance() {
      return (
        this.isConnected &&
        this.visible &&
        this.pageVisible &&
        !this.hovered &&
        !this.reduceMotion
      );
    }

    _startLoop() {
      if (this._loopToken) this._loopToken.cancelled = true;
      var token = { cancelled: false };
      this._loopToken = token;
      this._runLoop(token);
    }

    _runLoop(token) {
      var self = this;

      function tick() {
        if (token.cancelled || !self.isConnected) return;

        wait(self.pauseMs).then(function () {
          if (token.cancelled || !self.isConnected) return;

          if (!self._canAdvance()) {
            wait(200).then(tick);
            return;
          }

          self._advancing = true;
          self.index += 1;
          self._applyTransform(true);
        });
      }

      tick();
    }

    _onTransitionEnd(event) {
      if (event.target !== this.surface || event.propertyName !== 'transform') return;
      if (!this._advancing) return;

      this._advancing = false;

      if (this.index >= this.originalSlides.length) {
        this.index = 0;
        this._applyTransform(false);
        // Force reflow so the next animated step works after the reset.
        void this.surface.offsetWidth;
      }

      if (this._loopToken && !this._loopToken.cancelled) {
        this._runLoop(this._loopToken);
      }
    }

    _onIndicatorClick(event) {
      var button = event.currentTarget;
      var nextIndex = parseInt(button.getAttribute('data-index'), 10);
      if (isNaN(nextIndex) || nextIndex === this._displayIndex()) return;
      this._goTo(nextIndex, true);
    }

    _onMouseEnter() {
      this.hovered = true;
    }

    _onMouseLeave() {
      this.hovered = false;
    }

    _onVisibility() {
      this.pageVisible = !document.hidden;
    }

    _onResize() {
      this._syncSlideWidths();
      this._applyTransform(false);
    }
  }

  if (!customElements.get('effilo-hero-scroll')) {
    customElements.define('effilo-hero-scroll', EffiloHeroScroll);
  }
})();
